import { Box, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode } from 'react';

export type ColouredLabelBoxColor = 'primary' | 'success' | 'default';

const colorMap: Record<ColouredLabelBoxColor, { bg: string; text: string }> = {
    primary: {
        bg: 'primary.light',
        text: 'primary.main',
    },
    success: {
        bg: 'success.light',
        text: 'success.dark',
    },
    default: {
        bg: 'grey.200',
        text: 'text.primary',
    },
};

const defaultLabelBoxStyles = {
    display: 'inline-block',
    width: 'fit-content',
    maxWidth: '100%',
    borderRadius: 1,
    px: 1.5,
    py: 0.75,
};

const defaultLabelBoxTypographyStyles = {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
};

export type ColouredLabelBoxProps = {
    /** Content to show inside the box (e.g. role name, status) */
    children: ReactNode;
    /** Visual variant: primary (blue), success (green), default (grey) */
    color?: ColouredLabelBoxColor;
    /** Optional MUI sx overrides */
    sx?: SxProps<Theme>;
    /** Optional data-testid for the root Box */
    dataTestId?: string;
};

/**
 * Reusable coloured box that displays a label or value (e.g. role, status).
 * Use color="primary" for blue, color="success" for green.
 */
const ColouredLabelBox = ({ children, color = 'default', sx, dataTestId }: ColouredLabelBoxProps) => {
    const { bg, text } = colorMap[color];
    return (
        <Box
            data-testid={dataTestId}
            sx={{
                ...defaultLabelBoxStyles,
                backgroundColor: bg,
                color: text,
                ...sx,
            }}
        >
            <Typography component="span" variant="body2" fontWeight={500} sx={defaultLabelBoxTypographyStyles}>
                {children}
            </Typography>
        </Box>
    );
};

export default ColouredLabelBox;
